import type { SpendableCoin } from '~/composables/useGoby';
import type { Profile } from '~/types/generated';

export interface Did {
  name: string;
  didId: string;
  coinId: string;
}

export interface Collection {
  id: string;
  name: string;
  description: string;
  iconUrl?: string;
  bannerUrl?: string;
  twitterHandle?: string;
  website?: string;
  licenseUrl?: string;
  licenseHash?: string | null;
  attributes: { name: string }[];
}

export interface NftMetadata {
  format: 'CHIP-0007';
  minting_tool: 'mintgarden-studio';
  sensitive_content: boolean;
  name: string;
  description: string;
  attributes: { name: string; value: string }[];
}

export interface NftOnChainMetadata {
  royaltyPercentage: number;
  targetAddress?: string;
  royaltyAddress?: string;
  licenseUrl?: string;
  licenseHash?: string;
}

export type JobStatus = 'draft' | 'ready' | 'minting' | 'done';
export type MintStatus = 'ready' | 'uploading' | 'minting' | 'pending' | 'offering' | 'done';

interface BaseNftData {
  name?: string;
  file?: File;
  urisAndHashes?: {
    dataUris: string[];
    dataHash: string;
    metadataUris: string[];
    metadataHash: string;
    edition_number?: number;
    edition_total?: number;
  };
  requestedXch?: number;
  requestedNft?: string;
  nft?: { id: string; encodedId: string };
  offer?: { path: string; dexieId?: string };
  onChainMetadata?: { edition_number?: number; edition_total?: number };
}

export interface SingleNftData extends BaseNftData {
  metadata: any;
}

export interface BulkNftData extends BaseNftData {
  metadataPath: string;
}

export type NftData = SingleNftData | BulkNftData;

export interface MintTask {
  id: string;
  // type: 'single' | 'bulk'; TODO Necessary?
  status: MintStatus;
  nftDataIds: string[];
  incomingTxId?: string;
  targetAddress?: string; // Only used for random minting, use nftdata.onChainMetadata.target_address when bulk minting
  spentDidCoinId?: string;
  error?: { stage: MintStatus; error: any };
}

export type ProfileWithCoin = Profile & { didCoin: SpendableCoin; publicKey: string };

interface BaseMintJob {
  did: ProfileWithCoin;
  collectionId?: string;
  blockchainFee?: number;
  onChainMetadata: NftOnChainMetadata;
}

export interface SingleMintJob extends BaseMintJob {
  offerPriceXch?: number;
  uploadOfferToDexie?: boolean;
  nfts: SingleNftData[];
}

interface BaseBulkMintJob extends BaseMintJob {
  type: 'bulk';
  directoryPath: string;
  paymentMethod: 'upfront' | 'royalty';
  nfts: BulkNftData[];
  automaticallyAddSeries?: boolean;
}

export interface DirectBulkMintJob extends BaseBulkMintJob {
  mintStrategy: 'bulk';
  createOfferFiles?: boolean;
  offerPriceXch?: number;
  uploadOfferToDexie?: boolean;
}

export interface RandomBulkMintJob extends BaseBulkMintJob {
  mintStrategy: 'random';
  startTime: number | undefined;
  paymentAddress: string;
  paymentPrice: number;
  paymentAssetId: string | undefined;
}

export type BulkMintJob = DirectBulkMintJob | RandomBulkMintJob;

export type MintJob = SingleMintJob | BulkMintJob;

export const initialMetadata = {
  format: 'CHIP-0007',
  minting_tool: 'mintgarden-studio',
  sensitive_content: false,
  name: '',
  description: '',
  attributes: [],
};
export const initialOnChainMetadata = {
  royaltyPercentage: 0,
  targetAddress: undefined,
  royaltyAddress: undefined,
  licenseUrl: undefined,
  licenseHash: undefined,
};

export interface Auction {
  id: string;
  nftIds: string[];
  startTimestamp: number;
  secondsPerStep: number;
  cooldownDuration: number;
  startingPrice: number;
  reservePrice: number;
  stepSize: number;
  auctionBaseUrl: string;
}
